<template>
  <Drawer
    v-model:visible="isVisible"
    header="Profiles on this email"
    position="right"
    class="!w-full md:!w-[500px]"
    :modal="true"
    block-scroll
  >
    <div class="flex flex-col gap-3">
      <p class="text-sm text-gray-600">{{ email }}</p>

      <div v-if="isLoading" class="flex justify-center p-6">
        <i class="pi pi-spin pi-spinner text-2xl text-gray-400" />
      </div>

      <p v-else-if="profiles.length === 0" class="text-sm text-gray-500">
        No other profiles on this email.
      </p>

      <button
        v-for="profile in profiles"
        v-else
        :key="profile.id"
        type="button"
        class="flex items-center gap-3 border rounded-md p-3 text-left hover:border-primary"
        :data-testid="`shared-profile-${profile.id}`"
        @click="emit('select', profile.id)"
      >
        <UserAvatar
          :initials="getUserInitials(profile)"
          :asset="profile.picture"
          size="normal"
        />
        <div class="flex flex-col gap-0.5 min-w-0">
          <div class="flex items-center gap-1">
            <span class="text-sm font-medium">{{ profile.name || "N/A" }}</span>
            <Tag
              v-for="role in getUserRoles(profile)"
              :key="role"
              :value="role"
              v-bind="getRoleTagProps(role)"
              class="text-xs"
            />
          </div>
          <span class="text-xs text-gray-500 truncate">
            {{ subtitle(profile) }}
          </span>
        </div>
      </button>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Drawer from "primevue/drawer";
import Tag from "primevue/tag";
import UserAvatar from "@/shared/components/UserAvatar.vue";
import { getUserInitials, getUserRoles, getRoleTagProps } from "../helpers";
import type { AdminProfile } from "@/api/model";

interface Props {
  visible?: boolean;
  email: string;
  profiles: readonly AdminProfile[];
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  isLoading: false,
});

const emit = defineEmits<{
  "update:visible": [value: boolean];
  select: [profileId: number];
}>();

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

/** Their team, or their own email when it differs from the shared one. */
function subtitle(profile: AdminProfile): string {
  const teams = profile.teams?.map((team) => team.name).filter(Boolean) ?? [];
  if (teams.length > 0) return teams.join(", ");
  if (profile.email && profile.email !== props.email) return profile.email;
  return "No team";
}
</script>
